import { useFeatureAuth } from "@/services/backend/auth/hooks/use-feature-auth";
import {
  GetSamplingMetadataResponse,
  UpdateSamplingMetadata,
  useGetDeliveryByProcessQuery,
  useGetSamplingMetadataQuery,
  useUpdateSamplingMetadataMutation,
} from "@/services/backend/deliveries/delivery/service";
import { Testnorm } from "@/services/backend/types";
import { testnorm } from "@/shared/components/enum-labels";
import { Button } from "@/shared/components/ui/button";
import { Calendar } from "@/shared/components/ui/calendar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { formatDate } from "@/shared/utilities/date";
import { skipToken } from "@reduxjs/toolkit/query";
import { de } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { ReactElement, useState } from "react";
import { useParams } from "react-router";

export function SamplingMetadataIndexRoute(): ReactElement {
  const { processId } = useParams();
  const { data } = useGetDeliveryByProcessQuery({
    processId: processId as string,
  });
  const { data: samplingMetadata } = useGetSamplingMetadataQuery(
    data ? { deliveryId: data.id } : skipToken,
  );

  if (samplingMetadata == null || data == null) {
    return <div />;
  }

  return (
    <SamplingMetadataCard
      id={data.id}
      samplingMetadata={samplingMetadata}
      immutable={data.immutable}
    />
  );
}

interface SamplingMetadataCardProps {
  id: string;
  samplingMetadata: GetSamplingMetadataResponse;
  immutable: boolean | undefined;
}

function SamplingMetadataCard({
  id,
  samplingMetadata,
  immutable,
}: SamplingMetadataCardProps): ReactElement {
  const canEdit = useFeatureAuth("inspections.delivery.edit");
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [request, setRequest] = useState<UpdateSamplingMetadata>({
    id,
    activeAirMicrobialMetadata: samplingMetadata.activeAirMicrobialMetadata,
    samplingMetadata: samplingMetadata.samplingMetadata,
  });

  const [updateSamplingMetadata, { isLoading, error, isSuccess, reset }] =
    useUpdateSamplingMetadataMutation();
  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    updateSamplingMetadata,
    isLoading,
    error,
    isSuccess,
    reset,
    0,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-3xl">Probenahmedaten</CardTitle>
        <CardDescription>
          Die Daten der Proben für die gesamte Lieferung
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col space-y-4">
        <div className="flex w-full flex-col space-y-2">
          <Label htmlFor="sampledBy">Beprobung durch...</Label>
          <Input
            id="sampledBy"
            disabled={!canEdit || immutable}
            defaultValue={request.samplingMetadata.sampledBy}
            onChange={(e) =>
              setRequest({
                ...request,
                samplingMetadata: {
                  ...request.samplingMetadata,
                  sampledBy: e.target.value,
                },
              })
            }
            placeholder="Beprobung durch..."
            type="text"
          />
        </div>
        <div className="flex w-full flex-col space-y-2">
          <Label htmlFor="sampleTakers">Probenehmer</Label>
          <Input
            id="sampleTakers"
            disabled={!canEdit || immutable}
            defaultValue={request.samplingMetadata.sampleTakers}
            onChange={(e) =>
              setRequest({
                ...request,
                samplingMetadata: {
                  ...request.samplingMetadata,
                  sampleTakers: e.target.value,
                },
              })
            }
            placeholder="Probenehmer"
            type="text"
          />
        </div>
        <div className="flex w-full flex-col space-y-2">
          <Label htmlFor="testnorm">Prüfgrundlage</Label>
          <Select
            disabled={!canEdit || immutable}
            defaultValue={request.samplingMetadata.testnorm}
            onValueChange={(e) =>
              setRequest({
                ...request,
                samplingMetadata: {
                  ...request.samplingMetadata,
                  testnorm: e as Testnorm,
                },
              })
            }
          >
            <SelectTrigger
              disabled={immutable}
              className="w-full"
              defaultValue={Testnorm.VDI}
            >
              <SelectValue placeholder="Prüfgrundlage" />
            </SelectTrigger>
            <SelectContent defaultValue={Testnorm.VDI}>
              {Object.values(Testnorm).map((value) => (
                <SelectItem key={value} value={value}>
                  {testnorm[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-full flex-col space-y-2">
          <Label htmlFor="sampleDate">Beginn der Probenahme</Label>
          <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button
                disabled={!canEdit || immutable}
                variant="outline"
                data-empty={request.samplingMetadata.sampleDate}
                className="data-[empty=true]:text-muted-foreground justify-start text-left font-normal"
              >
                <CalendarIcon />
                {request.samplingMetadata.sampleDate ? (
                  formatDate(request.samplingMetadata.sampleDate, de)
                ) : (
                  <span>Datum auswählen</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={new Date(request.samplingMetadata.sampleDate)}
                onSelect={(e) => {
                  setRequest({
                    ...request,
                    samplingMetadata: {
                      ...request.samplingMetadata,
                      sampleDate: e!.toISOString(),
                    },
                  });
                  setIsDatePickerOpen(false);
                }}
              />
            </PopoverContent>
          </Popover>
        </div>
      </CardContent>
    </Card>
  );
}
